import { getFromLS, isAdminVerify } from '@orientaction/utils';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { apiServiceNative } from '../../../../../utils/fetchNative';
import { getBeneficiaryResourcesBackPath } from '../../../Tests/shared/adminResultNav';
import { getQuestionType, getSelectedQcmIds, toStoredQcmUser } from '../utils/qcm';

type EbookParams = {
  id?: string;
  index?: string;
};

export type CustomEbookBlock = {
  id?: string | number;
  uniqueIdContent?: string;
  bloc_template?: number;
  contentForType?: 'normal' | 'citation' | 'encyclopedia';
  paragraphe?: string;
  citation?: string;
  author?: string;
  encyclopedia?: string;
  alignement?: string;
  title?: string;
  title_type?: 'subtitle' | string;
  qcm_title?: string;
  qcm?: Array<{
    type?: 'radio' | 'checkbox';
    response?: Array<{
      id: number;
      apiType: string;
      typeReponse?: string;
      labelRadio?: string;
    }>;
  }>;
  /** Radio: single id string. Checkbox: array of id strings from the API. */
  response_qcm_user?: string | number | Array<string | number>;
  response_qcm_text_user?: string;
  free_question_title?: string;
  response_free_question_user?: string;
  updateUrl?: string;
  numLines?: number;
  img?: string;
  checkedLink?: boolean;
  imgLink?: string;
  videoBook?: string;
  videoBookEn?: string;
  autoPlayVideo?: number | null;
  muteVideo?: number | null;
  autoPlayVideoEn?: number | null;
  muteVideoEn?: number | null;
};

const updateBlockAt = (
  blocks: CustomEbookBlock[],
  index: number,
  nextValues: Partial<CustomEbookBlock>
) =>
  blocks.map((block, blockIndex) => (blockIndex === index ? { ...block, ...nextValues } : block));

export const useCustomEbookReader = () => {
  const { id, index } = useParams<EbookParams>();
  const location = useLocation();
  const history = useHistory();
  const token = getFromLS('userToken') || '';
  const isDisconnected = !token;
  const signupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const query = new URLSearchParams(location.search);
  const queryUserId = query.get('userId') || '';
  const queryReadOnly = query.get('readOnly') === 'true';

  const [coverUrl, setCoverUrl] = useState('');
  const [catalogEbookId, setCatalogEbookId] = useState<number | ''>('');
  const [blocks, setBlocks] = useState<CustomEbookBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showSignup, setShowSignup] = useState(false);
  const [readOnly, setReadOnly] = useState(queryReadOnly || isAdminVerify());
  const [ownerUserId, setOwnerUserId] = useState(queryUserId);

  useEffect(() => {
    const fetchEbook = async () => {
      if (!id) {
        return;
      }

      setLoading(true);
      setLoadError('');
      setBlocks([]);
      setCoverUrl('');

      try {
        const response = await apiServiceNative.get(`/api/ebook-front-user/${id}`);
        const ebook = response?.ebook;
        if (!ebook) throw new Error('Ebook unavailable');
        if (response.id && String(response.id) !== id) {
          history.replace(`/ebook/view/${response.id}/${index || '0'}${location.search}`);
        }
        const ownerId = Number(response?.user?.id);
        const currentUser = JSON.parse(getFromLS('userInfos') || '{}');
        const isOwner = ownerId && Number(currentUser?.id) === ownerId;

        if (ebook) {
          setBlocks(Array.isArray(ebook.content) ? ebook.content : []);
          setCoverUrl(ebook.couverture || '');
          if (ebook.id) {
            setCatalogEbookId(Number(ebook.id));
          }
        }
        if (ownerId) {
          setOwnerUserId(String(ownerId));
        }
        setReadOnly(queryReadOnly || isAdminVerify() || Boolean(ownerId && !isOwner));
      } catch (error) {
        setLoadError('Cet e-book n’est pas disponible.');
        console.error('Error fetching ebook details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEbook();
  }, [id, queryReadOnly, history, index, location.search]);

  useEffect(() => {
    if (!isDisconnected) {
      setShowSignup(false);
      return undefined;
    }

    signupTimerRef.current = setTimeout(() => {
      setShowSignup(true);
    }, 30000);

    return () => {
      if (signupTimerRef.current) {
        clearTimeout(signupTimerRef.current);
      }
    };
  }, [isDisconnected, id]);

  const updateQCM = async (apiType: string, blockIndex: number, responseId: number) => {
    if (readOnly) {
      return;
    }
    let snapshot: CustomEbookBlock[] | null = null;

    setBlocks(currentBlocks => {
      snapshot = currentBlocks;
      const block = currentBlocks[blockIndex];
      if (!block) return currentBlocks;

      const questionType = getQuestionType(block);
      const current = getSelectedQcmIds(block);
      const next =
        questionType === 'checkbox'
          ? current.includes(responseId)
            ? current.filter(selectedId => selectedId !== responseId)
            : [...current, responseId]
          : [responseId];

      return updateBlockAt(currentBlocks, blockIndex, {
        response_qcm_user: toStoredQcmUser(questionType, next),
      });
    });

    try {
      await apiServiceNative.put(`/api${apiType}`, {});
    } catch (error) {
      console.error(error);
      if (snapshot) {
        setBlocks(snapshot);
      }
    }
  };

  const updateQCMText = async (
    apiType: string,
    blockIndex: number,
    responseId: number,
    value: string
  ) => {
    if (readOnly) {
      return;
    }
    setBlocks(previousBlocks => {
      const block = previousBlocks[blockIndex];
      if (!block) return previousBlocks;

      const questionType = getQuestionType(block);
      let nextSelected = getSelectedQcmIds(block);

      if (questionType === 'radio') {
        nextSelected = [responseId];
      } else if (!nextSelected.includes(responseId)) {
        nextSelected = [...nextSelected, responseId];
      }

      return updateBlockAt(previousBlocks, blockIndex, {
        response_qcm_user: toStoredQcmUser(questionType, nextSelected),
        response_qcm_text_user: value,
      });
    });

    try {
      await apiServiceNative.put(`/api${apiType}`, { response: value });
    } catch (error) {
      console.error(error);
    }
  };

  const updateFreeQuestion = async (apiType: string, blockIndex: number, value: string) => {
    if (readOnly) {
      return;
    }
    setBlocks(previousBlocks =>
      updateBlockAt(previousBlocks, blockIndex, { response_free_question_user: value })
    );

    try {
      await apiServiceNative.put(`/api${apiType}`, { response: value });
    } catch (error) {
      console.error(error);
    }
  };

  const toBack = isDisconnected
    ? ''
    : isAdminVerify() && ownerUserId
    ? getBeneficiaryResourcesBackPath(ownerUserId)
    : '/mes-ressources';

  return {
    blocks,
    coverUrl,
    catalogEbookId,
    ebookId: id,
    index: index || '0',
    isDisconnected,
    loading,
    loadError,
    readOnly,
    showSignup,
    toBack,
    updateFreeQuestion,
    updateQCM,
    updateQCMText,
  };
};
